import React from 'react';
import { Dialog } from '../../dialog';
/**
 * Props for the UnsavedChangesModal component.
 * Configures the modal's behavior, appearance, and callback handlers for unsaved changes scenarios.
 */
interface UnsavedChangesModalProps {
  /** Whether the modal is currently open and visible. */
  isOpen: boolean;
  /** Callback invoked when user chooses to proceed with navigation despite unsaved changes. */
  onProceed: () => void | Promise<void>;
  /** Callback invoked when user chooses to stay on the current page. */
  onStay: () => void;
  /** Whether the modal is in a saving state (disables interactions, shows loading state). */
  isSaving?: boolean;
  /** Title text displayed at the top of the modal. Defaults to Spanish "Cambios sin guardar". */
  title?: string;
  /** Message/description text explaining the unsaved changes situation. */
  message?: string;
  /** Label for the primary action button (proceed with navigation). */
  saveButtonText?: string;
  /** Label for the secondary action button (stay on current page). */
  discardButtonText?: string;
}

/**
 * Modal component that prompts users when attempting to navigate away with unsaved changes.
 * Integrates with the unsaved navigation guard to prevent accidental data loss.
 *
 * @example
 * const { isBlocked, proceed, stay } = useUnsavedNavigationGuard(isDirty);
 * return (
 *   <UnsavedChangesModal
 *     isOpen={isBlocked}
 *     onProceed={proceed}
 *     onStay={stay}
 *   />
 * );
 */
export const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  isOpen,
  onProceed,
  onStay,
  isSaving = false,
  // TODO: check when implementing i18n
  title = 'Cambios sin guardar',
  message = 'Tienes cambios sin guardar. Si continúas, se perderán. ¿Deseas continuar?',
  saveButtonText = 'Continuar sin guardar',
  discardButtonText = 'Permanecer',
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onStay}
      title={title}
      description={message}
      confirmLabel={saveButtonText}
      cancelLabel={discardButtonText}
      onConfirm={onProceed}
      isLoading={isSaving}
      variant="danger"
    />
  );
};
